import type { AnyContext, Environment } from "./types.js";

/**
 * Lightweight UA classification. We do not aim for perfect parity with
 * commercial UA parsers — server-side enrichment can refine these. We
 * just want a coarse signal that's cheap to compute in the browser.
 */
function classifyUA(ua: string | undefined): { browser?: string; os?: string; deviceType?: AnyContext["deviceType"] } {
  if (!ua) return {};
  const u = ua.toLowerCase();

  let browser: string | undefined;
  if (u.includes("edg/")) browser = "edge";
  else if (u.includes("opr/") || u.includes("opera")) browser = "opera";
  else if (u.includes("chrome/")) browser = "chrome";
  else if (u.includes("firefox/")) browser = "firefox";
  else if (u.includes("safari/")) browser = "safari";

  let os: string | undefined;
  if (u.includes("windows")) os = "windows";
  else if (u.includes("mac os")) os = "macos";
  else if (u.includes("android")) os = "android";
  else if (u.includes("iphone") || u.includes("ipad") || u.includes("ipod")) os = "ios";
  else if (u.includes("linux")) os = "linux";

  let deviceType: AnyContext["deviceType"] = "desktop";
  if (u.includes("ipad") || u.includes("tablet")) deviceType = "tablet";
  else if (u.includes("mobile") || u.includes("android") || u.includes("iphone")) deviceType = "mobile";
  if (/(bot|crawler|spider|crawling)/.test(u)) deviceType = "bot";

  return { browser, os, deviceType };
}

function viewport(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return `${window.innerWidth}x${window.innerHeight}`;
}

function screenRes(): string | undefined {
  if (typeof window === "undefined" || !window.screen) return undefined;
  return `${window.screen.width}x${window.screen.height}`;
}

function tz(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export function buildContext(env: Environment | undefined): AnyContext {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : undefined;
  const { browser, os, deviceType } = classifyUA(ua);
  return {
    url: typeof location !== "undefined" ? location.href : undefined,
    path: typeof location !== "undefined" ? location.pathname + location.search : undefined,
    referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
    userAgent: ua,
    language: typeof navigator !== "undefined" ? navigator.language : undefined,
    screenResolution: screenRes(),
    viewport: viewport(),
    timezone: tz(),
    deviceType,
    browser,
    os,
    environment: env,
  };
}
